import React, { useCallback, useEffect, useState } from 'react';
import swal from 'sweetalert2';
import AppLoader from 'components/Loader';
import { useDispatch, useSelector } from 'react-redux';
import { resetAddingContributor } from 'redux/actions/contributor';
import { addContributor } from 'redux/thunks/contributor';
import { validEmail } from 'utilities/Validations';
import { Button, Grid, TextField, Typography } from '@material-ui/core';
import Modal from 'components/ModalWrapper';
import './AddContributorModal.css';

const AddContributorModal = ({ show, onClose, siteId, dashboardId }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const contributor = useSelector((state) => state.contributor);
    const dispatch = useDispatch();

    const close = useCallback(() => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setFieldErrors({});
        onClose();
    }, [onClose]);

    const validate = () => {
        let errors = {};

        if (!firstName) {
            errors['first-name'] = '';
        }

        if (!lastName) {
            errors['last-name'] = '';
        }

        if (!email) {
            errors['email'] = '';
        } else {
            const emailValid = validEmail(email);
            if (!emailValid) {
                errors['email'] = 'Please enter a valid email address';
            }
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        const contributor = {
            firstName: firstName,
            lastName: lastName,
            email: email,
            dashboardId,
            siteId,
        };

        dispatch(addContributor(contributor));
    };

    useEffect(() => {
        if (contributor.addingContributorSuccess) {
            swal.fire({
                type: 'success',
                title: 'Success',
                text: 'Contributor successfully added!',
                confirmButtonText: 'Ok',
            });
            dispatch(resetAddingContributor());
            close();
        }
    }, [contributor.addingContributorSuccess, close, dispatch]);

    return (
        <Modal allowClickOutsideToClose height='small' open={show} onCloseClicked={close}>
            <Grid container className='add-contributor-modal'>
                <Grid container item justifyContent='center' className='add-contributor-modal__title'>
                    <Typography variant='h5'>Add a Contributor</Typography>
                </Grid>

                <Grid container direction='column'>
                    {contributor.addingContributor ? (
                        <AppLoader />
                    ) : (
                        <>
                            <TextField
                                className='add-contributor-modal__form-field'
                                variant='outlined'
                                label='First Name *'
                                error={'first-name' in fieldErrors}
                                helperText={'first-name' in fieldErrors && 'This field is required'}
                                value={firstName}
                                onChange={(event) => setFirstName(event.target.value)}
                            />

                            <TextField
                                className='add-contributor-modal__form-field'
                                variant='outlined'
                                label='Last Name *'
                                error={'last-name' in fieldErrors}
                                helperText={'last-name' in fieldErrors && 'This field is required'}
                                value={lastName}
                                onChange={(event) => setLastName(event.target.value)}
                            />

                            <TextField
                                className='add-contributor-modal__form-field'
                                variant='outlined'
                                label='Email *'
                                error={'email' in fieldErrors}
                                helperText={'email' in fieldErrors && (fieldErrors['email'] || 'This field is required')}
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                            />
                        </>
                    )}
                </Grid>

                <Grid container justifyContent='space-between' className='add-contributor-modal__footer'>
                    <Button
                        color='default'
                        size='large'
                        variant='contained'
                        onClick={close}
                        disabled={contributor.addingContributorSuccess}
                        className='add-contributor-modal__footer-button--close'
                    >
                        Cancel
                    </Button>
                    <Button
                        color='primary'
                        size='large'
                        variant='contained'
                        type='submit'
                        disabled={contributor.addingContributorSuccess}
                        onClick={() => handleSubmit()}
                        className='add-contributor-modal__footer-button--save'
                    >
                        Save Changes
                    </Button>
                </Grid>
            </Grid>
        </Modal>
    );
};

export default AddContributorModal;
